import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import BrandingTab from '@/features/settings/BrandingTab'
import FieldsTab from '@/features/settings/FieldsTab'
import ContactFormTab from '@/features/settings/ContactFormTab'
import PipelineTab from '@/features/settings/PipelineTab'
import TemplatesTab from '@/features/settings/TemplatesTab'
import SendingDomainTab from '@/features/settings/SendingDomainTab'
import PageContainer from '@/components/layout/PageContainer'

const TABS = [
  { id: 'branding', label: 'Branding', hint: 'Your logo, colours, currency, timezone and who gets escalations — applied across the app.' },
  {
    id: 'contact-form',
    label: 'Contact form',
    hint: 'The details on every contact in the CRM (the Add contact form and the contact card): order, rename or hide them. Not your website forms — those are under Forms.',
  },
  { id: 'fields', label: 'Custom fields', hint: 'Your own fields on contacts and deals — e.g. Property type, Budget, Settlement date.' },
  { id: 'templates', label: 'Templates', hint: 'Reusable emails and SMS. Workflows and sequences that use a template send what you save here.' },
  { id: 'pipeline', label: 'Pipeline', hint: 'The stages deals move through, grouped into sections, and when a deal is flagged for follow-up.' },
  { id: 'email', label: 'Sending domain', hint: "Let agents send from their own address (agent@youragency.com.au) — set up once by your IT." },
] as const

type TabId = (typeof TABS)[number]['id']

function isTabId(v: string | null): v is TabId {
  return TABS.some((t) => t.id === v)
}

export default function Settings() {
  // Deep-linkable via /settings?tab=<id> (e.g. the "Manage fields" gear in detail panels).
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab')
  const [tab, setTab] = useState<TabId>(isTabId(initialTab) ? initialTab : 'branding')

  const current = TABS.find((t) => t.id === tab) ?? TABS[0]

  return (
    <PageContainer width="full">
      <h1 className="text-2xl font-semibold text-brand-darkest mb-1">Settings</h1>
      <p className="text-sm text-gray-500 mb-5">How your workspace looks and works — changes apply for the whole team.</p>

      <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
        {/* Sections down the side on a wide screen; a sideways-scrolling strip on a phone. */}
        <nav aria-label="Settings sections" className="overflow-x-auto lg:overflow-visible">
          <ul className="flex lg:flex-col gap-1 w-max lg:w-auto">
            {TABS.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setTab(t.id)}
                  aria-current={tab === t.id ? 'page' : undefined}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm whitespace-nowrap ${
                    tab === t.id ? 'bg-brand-primary/10 text-brand-primary font-medium' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section className="min-w-0">
          <h2 className="text-lg font-semibold text-brand-darkest">{current.label}</h2>
          <p className="text-sm text-gray-500 mb-5 max-w-3xl">{current.hint}</p>
          {tab === 'branding' && <BrandingTab />}
          {tab === 'contact-form' && <ContactFormTab />}
          {tab === 'fields' && <FieldsTab />}
          {tab === 'templates' && <TemplatesTab />}
          {tab === 'pipeline' && <PipelineTab />}
          {tab === 'email' && <SendingDomainTab />}
        </section>
      </div>
    </PageContainer>
  )
}
